import jwtDecode from 'jwt-decode';
import { createContext, useState, useEffect, useContext, useMemo, useRef } from 'react';
import { getUserDataByEmail, postSamlLoginExchange } from '../services/entity/user/user-api';
import { isTokenExpired, setCurrentTokenInBrowserStorage } from '../helpers/token/token-helper';
import { useNavigate } from 'react-router-dom';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const navigate = useNavigate();

  const initializationStarted = useRef(false);

  const [user, setUser] = useState(null);
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.has('saml_code');
  });

  const getUserData = async () => {
    setIsUserDataLoading(true);

    try {
      const url = new URL(window.location.href);
      const samlCode = url.searchParams.get('saml_code');

      if (samlCode) {
        setIsSsoAuthenticating(true);

        url.searchParams.delete('saml_code');
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

        const samlUserData = await postSamlLoginExchange(samlCode);

        if (samlUserData) {
          setCurrentTokenInBrowserStorage(samlUserData.token);
          setUser(samlUserData.user);
          return;
        }
      }

      const token = localStorage.getItem('token');

      if (token) {
        if (!isTokenExpired(token)) {
          const payload = jwtDecode(token);
          const userData = await getUserDataByEmail(token, payload.username);

          setUser(userData);
        } else {
          localStorage.removeItem('token');
        }
      }
    } catch (err) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setIsSsoAuthenticating(false);
      setIsUserDataLoading(false);
    }
  };

  const updateUser = (newUser) => {
    setUser(newUser.user);
    setCurrentTokenInBrowserStorage(newUser.token);
  };

  const logoutUser = () => {
    navigate('/production');
    setUser(null);
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    window.location.reload();
  };

  useEffect(() => {
    if (initializationStarted.current) {
      return;
    }

    initializationStarted.current = true;
    getUserData();
  }, []);

  const value = useMemo(
    () => ({
      user,
      updateUser,
      logoutUser,
      setUser,
      isUserDataLoading,
      isSsoAuthenticating,
    }),
    [
      user,
      isUserDataLoading,
      isSsoAuthenticating,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser doit être utilisé au sein d'un UserProvider");
  }

  return context;
};